import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { IMAGES, TESTIMONIALS } from "../mock";
import { Reveal, PageHero } from "../components/Shared";
import Seo from "../components/Seo";

export default function RacingManagement() {
  return (
    <div data-testid="racing-management-page">
      <Seo
        title="Racing Management"
        description="Bespoke, unique and adaptable racing management. From point of purchase to the racecourse and beyond — transparency, accountability and your interests first."
        image={IMAGES.heroRacing}
      />
      <PageHero image={IMAGES.heroRacing} kicker="Our Services" title="Racing Management">
        <p className="text-[#b9c9d6] mt-6 max-w-2xl text-lg leading-relaxed">
          As a team Global Equine Group stands shoulder to shoulder with our clients on the journey
          through racehorse ownership. Because we are passionate and as invested in the horses as
          our clients, your success is our success.
        </p>
      </PageHero>

      {/* Intro copy */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal>
              <p className="gee-kicker mb-4">A Modern Approach</p>
              <h2 className="font-serif-d text-[#0f2438] text-3xl md:text-4xl leading-tight mb-6">
                Flexible Enough To Suit Any Requirement
              </h2>
              <div className="gee-divider mb-8" />
            </Reveal>
            <Reveal delay={1}>
              <p className="text-[#5b6d7d] leading-relaxed mb-6">
                We have a bespoke, unique and adaptable racing management model, flexible enough to
                suit any requirements that our clients require. We do not just buy you a horse and
                then walk away, never to be seen again until the winners enclosure or the next
                sales. We are there with our owners through all the ups and downs of racehorse
                ownership, from point of purchase to the racecourse and beyond.
              </p>
              <p className="text-[#5b6d7d] leading-relaxed mb-8">
                In an archaic industry we bring a modern business approach to your horses. As your
                representative, we believe in transparency, accountability and an open flow of
                information at all times. We never forget that we are acting on behalf of our
                clients, and are there to represent their best interests.
              </p>
              <ul className="space-y-3">
                {["Transparency", "Accountability", "Open flow of information", "Your interests first"].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#33475a]">
                    <CheckCircle2 size={18} className="text-[#29abe2] shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={1}>
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-full h-full border-2 border-[#eaf5fc] hidden md:block" />
              <div className="gee-img-frame aspect-square relative">
                <img src={IMAGES.morningExercise} alt="Morning exercise in Newmarket" loading="lazy" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-24 bg-[#f7fbfd]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <p className="gee-kicker mb-4">Striving For Success</p>
            <h2 className="font-serif-d text-[#0f2438] text-3xl md:text-5xl mb-6">Our Philosophy</h2>
            <div className="gee-divider mx-auto mb-8" />
          </Reveal>
          <Reveal delay={1}>
            <p className="text-[#5b6d7d] text-lg leading-relaxed mb-10">
              At Global Equine Group we have a philosophy that the more we put in, the more you get
              out. Relax with a dedicated team behind your racing investments, protecting your
              interests, searching for value and striving for success.
            </p>
            <Link to="/team" className="gee-btn" data-testid="philosophy-team-link">
              Meet The Team <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Testimonial */}
      <section className="relative py-28 overflow-hidden" style={{ backgroundColor: "#081826" }}>
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url(${IMAGES.heroRacing})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-[1000px] mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <div className="gee-quote-mark select-none" aria-hidden="true">“</div>
            <blockquote className="font-serif-d text-white text-xl md:text-2xl leading-relaxed italic">
              {TESTIMONIALS[1].quote}
            </blockquote>
            <p className="mt-8 text-[#29abe2] font-medium">{TESTIMONIALS[1].name}</p>
            <p className="text-[#7d94a6] text-sm mt-1 uppercase tracking-[0.15em]">{TESTIMONIALS[1].role}</p>
          </Reveal>
        </div>
      </section>

      {/* Bloodstock Management */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="gee-img-frame aspect-[4/3]">
              <img src={IMAGES.bloodstock} alt="Bloodstock management" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="gee-kicker mb-4">From Foal To The Winning Post</p>
              <h2 className="font-serif-d text-[#0f2438] text-3xl md:text-4xl leading-tight mb-6">
                Bloodstock Management
              </h2>
              <div className="gee-divider mb-8" />
            </Reveal>
            <Reveal delay={1}>
              <p className="text-[#5b6d7d] leading-relaxed mb-6">
                Be it commercial breeding for profit or the dream of breeding a Derby winner, Global
                Equine Group will be there every step of the way, assisting and advising our clients
                to achieve their goals.
              </p>
              <p className="text-[#5b6d7d] leading-relaxed mb-6">
                On the farm we regularly assess the young stock, working closely with stud staff,
                vets, nutritionists and specialist farriers to ensure that the foals and yearlings
                grow up strong, straight and speedy! Our view is that prevention is much better, and
                easier, than cure, so we are ever vigilant for any issues that may affect the
                commercial value or the racing potential of our client's young stock.
              </p>
              <p className="text-[#5b6d7d] leading-relaxed mb-8">
                Using our extensive knowledge of the bloodstock and racing industry, we are able to
                provide a global view of the business — extensive statistical analysis, personal
                evaluation of current stallions and insight into which first-season sires are
                producing the best yearlings, all to help maximise the breeding potential of our
                client's mares.
              </p>
              <Link to="/blog" className="gee-link text-[#0f2438]" data-testid="bloodstock-news-link">
                Latest From The Farm <ArrowRight size={16} className="gee-link-arrow" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
